/* eslint-disable @typescript-eslint/no-explicit-any */
import classNames from 'classnames';
import { redrawProjectAction } from 'pages/ChartEditorPage/actions/chartEditor';
import TableComponent from 'pages/TableEditorPage/components/TableComponent';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import IconButton from 'shared/buttons/IconButton';
import { useIsMobile } from 'shared/utils/mediaHelper';
import { hideInlineDeleteButton } from 'shared/widgets/richtextgroup/DeleteButton';

type DataGridPreviewProps = {
  activeSeries: number[];
  isTable?: boolean;
  redrawOnRender?: boolean;
  large?: boolean;
  isPastingActive?: boolean;
  parent?: any;
  inEditor?: boolean;
};

let resizeObserver: ResizeObserver;
let resizeTimeout: NodeJS.Timeout;

const ChartPreview = ({
  activeSeries,
  isTable,
  redrawOnRender,
  large,
  isPastingActive,
  parent,
  inEditor
}: DataGridPreviewProps) => {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const [previewSizes, setPreviewSizes] = useState({
    small: { width: 300, height: 250 },
    regular: { width: 400, height: 350 },
    minimized: { width: 24, height: 24 },
    large: { width: '100%', height: '100%' }
  });
  const [chartOpen, setChartOpen] = useState(true);
  const [chartSize, setChartSize] = useState(large ? previewSizes.large : previewSizes.small);
  const { tab } = useSelector((state: RootState) => state.projectConfig);
  const chart: any = useSelector((state: RootState) => state.chartEditorPage.chart);

  const isSmall = chartSize === previewSizes.small;
  const inDataTab = tab === 'data';
  const isDataTabOnMobile = isMobile && inDataTab;

  useEffect(() => setChartSize(large ? previewSizes.large : previewSizes.small), [large]);
  useEffect(() => {
    hideInlineDeleteButton();
  }, [chartSize]);

  useEffect(() => {
    if (chart?.series) {
      chart.series.forEach((s: any, i: number) => {
        if (activeSeries.length) {
          if (activeSeries.includes(i)) s.setState('hover');
          else s.setState('inactive');
        } else s.setState('normal');
      });
    }
  }, [activeSeries]);

  useEffect(() => {
    if (tab === 'data' && parent?.current) {
      const smallWidth = parent.current.offsetWidth * 0.25;
      const regularWidth = parent.current.offsetWidth * 0.4;
      const small = { width: smallWidth, height: (250 / 300) * smallWidth };
      const regular = { width: regularWidth, height: (350 / 400) * regularWidth };

      setPreviewSizes({
        ...previewSizes,
        small,
        regular
      });

      setChartSize(small);
    }
    hideInlineDeleteButton();
    if (isMobile) dispatch(redrawProjectAction({}));
  }, [tab]);

  useEffect(() => {
    if (redrawOnRender) {
      dispatch(redrawProjectAction({}));
    }
  }, []);

  useEffect(() => {
    if (resizeObserver) resizeObserver.disconnect();
    if (chart?.container) {
      resizeObserver = new ResizeObserver(function () {
        if (chart && chart.reflow) chart.reflow();
        // Highmaps doesnt play well with reflowing here. Call a final
        // redraw at the end of the resize to align everything properly
        if (resizeTimeout) clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          chart.redraw();
        }, 200);
      });
      resizeObserver.observe(chart.renderTo);
    }
  }, [chart]);

  const containerClass = classNames('bg-white transition-all z-[1] absolute bottom-[0px] right-[0px]', {
    'z-[100000]': isPastingActive,
    hidden: (!inEditor && isMobile) || (inEditor && isDataTabOnMobile)
  });

  return (
    <div className={containerClass} style={chartSize}>
      {!isTable && (
        <div id="highcharts-container" className="w-full h-full" style={{ display: chartOpen ? 'block' : 'none' }} />
      )}
      {isTable && (
        <div className={`preview-table ${isSmall ? ' small' : ' regular'}`}>{chartOpen && <TableComponent />}</div>
      )}
      <>
        {!large && chartOpen && (
          <IconButton
            icon="arrows-up-down"
            iconClass="-rotate-45"
            className="absolute top-0 left-0 w-6 h-6 bg-ev-grey"
            onClick={() => {
              setChartSize(chartSize === previewSizes.small ? previewSizes.regular : previewSizes.small);
            }}
          />
        )}
        {!large && (
          <IconButton
            icon={chartOpen ? 'xmark' : 'eye'}
            className="absolute bottom-0 right-0 w-6 h-6 bg-ev-grey"
            onClick={() => {
              setChartOpen(!chartOpen);
              setChartSize(chartOpen ? previewSizes.minimized : previewSizes.small);
            }}
            active={chartOpen}
          />
        )}
      </>
    </div>
  );
};

export default ChartPreview;
